import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import router from 'next/router';

import { link } from '@phoenix/core/frontend/shared/util/navigation/link.utils';
import { mapFromUnregisteredProduct } from '@phoenix/team-m/my-tool-shed/frontend/util/product/product.mapper';
import { FindProductsToImportDocument, FindProductsToImportQuery } from '@phoenix/team-m/my-tool-shed/frontend/domain/generated/graphql';

import { addToolStartEvent } from './tracking/add-tool.events';
import { useQueryFetcher } from './use-query-fetcher';

export const SEARCH_TOOLS_QUERY_KEY = 'searchTools';

export const useSearchTools = (redirect = true) => {
  const [isRemoved, setIsRemoved] = useState(false);
  const queryFetcher = useQueryFetcher<FindProductsToImportQuery>(FindProductsToImportDocument);

  const {
    data,
    error,
    refetch,
    isFetching: isSearching,
    remove,
    isFetchedAfterMount,
  } = useQuery({
    queryKey: [SEARCH_TOOLS_QUERY_KEY],
    queryFn: () => queryFetcher(),
    enabled: false,
    select: (r) => r.data?.findProductsToImport?.items.map(mapFromUnregisteredProduct),
    refetchOnMount: false,
  });

  const goToSearchResults = () => {
    addToolStartEvent.registeredProduct();
    router.push(link('/tool-shed/add-tool/search-results'));
  };

  useEffect(() => {
    if (data?.length && isFetchedAfterMount) {
      redirect && isFetchedAfterMount && goToSearchResults();
    }
  }, [isFetchedAfterMount, isSearching, data]);

  useEffect(() => {
    isSearching && setIsRemoved(false);
  }, [isSearching]);

  const resetSearchTools = () => {
    remove();
    setIsRemoved(true);
  };

  return {
    fetchSearchProducts: () => refetch(),
    isSearching,
    showError: !isSearching && !!error,
    toolsFound: data,
    goToSearchResults,
    resetSearchTools,
    isRemoved,
  };
};
